import { z } from "zod";
import { BlockReason, BlockTargetKind, UnblockReasonText } from "./blocklist.ts";
import { IsoDateTime, OperatorEnvironment, PlayerRef } from "./common.ts";
import { BlockEntryV1 } from "./explorer/common.ts";
import { BLOCK_COMMENT_MAX_LENGTH, BLOCK_COMMENT_MIN_LENGTH } from "./explorer/blocks.ts";
import { AuditRecordDtoV1, CheckFlagV1, JurisdictionCode } from "./ports.ts";

// Decision Engine command DTOs v1 (change split-core-into-trust-contexts, design D4/D5): the
// synchronous command ports `ListEntryAdministration` (called by the Explorer's block routes) and
// `TenantPolicyAdministration` (called by `operator-auth`'s `ConfigureEnvironment`). Commands, not
// replicas: the caller needs the validation result. The operator and the environment come from the
// caller's verified scope and the installation's configuration, never from request input; the
// caller authorizes the console user before it calls (ADR 0001 R2, R5).

/** The console user who acts (the caller already checked the permission). */
const ConsoleUserActorV1 = z.object({ type: z.literal("console_user"), id: z.uuid() }).strict();
/**
 * A process that configures without a console user (task 6.2 of split-core-into-trust-contexts):
 * the provisioning and test tooling that call `operator-auth`'s `ConfigureEnvironment` in process
 * with their own actor label (e.g. `test-support:provision-operator`). Never a person's data.
 */
const SystemActorV1 = z
  .object({ type: z.literal("system"), id: z.string().min(1).max(64) })
  .strict();

const BlockComment = z.string().min(BLOCK_COMMENT_MIN_LENGTH).max(BLOCK_COMMENT_MAX_LENGTH);

/**
 * The source IP of the console request that blocks or unblocks (spec explorer-access, "Audit of
 * sensitive actions"): a console user's IP, kept in the List Entry's audit record only (never a
 * player's IP, never in an event). Optional: absent when the caller has none.
 */
export const ConsoleSourceIpV1 = z.union([z.ipv4(), z.ipv6()]).meta({ id: "ConsoleSourceIpV1" });
export type ConsoleSourceIpV1 = z.infer<typeof ConsoleSourceIpV1>;

const blockSubjectBase = {
  operator_id: z.uuid(),
  environment: OperatorEnvironment,
  reason: BlockReason,
  /** Stays in the Decision Engine (and its audit); never in the List Entry events. */
  comment: BlockComment,
  /** Absent for a block without expiry; must be in the future. */
  expires_at: IsoDateTime.optional(),
  actor: ConsoleUserActorV1,
  source_ip: ConsoleSourceIpV1.optional(),
};

/**
 * Explorer → Decision Engine (`ListEntryAdministration.block`): blocks one `player_ref` or
 * `device_ref` (the body of `POST /explorer/v1/blocks`, plus the verified scope and the actor).
 * At most one active List Entry per subject (`already_blocked`).
 */
export const BlockSubjectCommandV1 = z
  .discriminatedUnion("target_kind", [
    z
      .object({ ...blockSubjectBase, target_kind: z.literal("player"), target_ref: PlayerRef })
      .strict(),
    z
      .object({ ...blockSubjectBase, target_kind: z.literal("device"), target_ref: z.uuid() })
      .strict(),
  ])
  .meta({ id: "BlockSubjectCommandV1" });
export type BlockSubjectCommandV1 = z.infer<typeof BlockSubjectCommandV1>;

/**
 * Explorer → Decision Engine (`ListEntryAdministration.unblock`): lifts one active List Entry
 * (the body of `POST /explorer/v1/blocks/{id}/unblock`, plus the verified scope and the actor).
 */
export const UnblockSubjectCommandV1 = z
  .object({
    operator_id: z.uuid(),
    environment: OperatorEnvironment,
    block_id: z.uuid(),
    reason: UnblockReasonText,
    actor: ConsoleUserActorV1,
    source_ip: ConsoleSourceIpV1.optional(),
  })
  .strict()
  .meta({ id: "UnblockSubjectCommandV1" });
export type UnblockSubjectCommandV1 = z.infer<typeof UnblockSubjectCommandV1>;

/**
 * Decision Engine → Explorer: the List Entry after a block or an unblock, with the same fields as
 * the Explorer's {@link BlockEntryV1} (the block routes answer it unchanged).
 */
export const ListEntryV1 = BlockEntryV1.meta({ id: "ListEntryV1" });
export type ListEntryV1 = z.infer<typeof ListEntryV1>;

/** Refusals of the List Entry commands (today's `BlockSubject` / `UnblockSubject` error codes). */
export const LIST_ENTRY_COMMAND_ERRORS = [
  "invalid_block_reason",
  "comment_too_short",
  "comment_too_long",
  "expiry_not_in_future",
  "invalid_target_kind",
  "invalid_target_ref",
  "already_blocked",
  "block_not_found",
  "block_not_active",
  "unblock_reason_required",
  "unblock_reason_too_long",
] as const;
export const ListEntryCommandRejectedV1 = z
  .object({ code: z.enum(LIST_ENTRY_COMMAND_ERRORS) })
  .strict()
  .meta({ id: "ListEntryCommandRejectedV1" });
export type ListEntryCommandRejectedV1 = z.infer<typeof ListEntryCommandRejectedV1>;

// List Entry queries v1 (task 7.1 of split-core-into-trust-contexts, design D14): what the Explorer
// reads from the Decision Engine since the List Entries live there (`core.list_entries`): the
// Blocklist screen (`GET /explorer/v1/blocks`), the current status of players and devices (badges,
// lists, profiles, exports, the map's status filter) and the List Entry records of the audit screen.
// Queries over the caller's verified scope (the operators where the console user holds the
// permission, the installation's environment), never request input.

/** Operators of one query (the Explorer's scope of a console user). */
const OperatorScope = z.array(z.uuid()).min(1).max(1000);

/**
 * Explorer → Decision Engine (`ListEntryAdministration.list`): the Blocklist screen's tab, status,
 * exact identifier, reason and creation period (`blocked_from` inclusive, `blocked_to` exclusive),
 * newest first (`blocked_at`, then id), paginated.
 */
export const ListEntryQueryV1 = z
  .object({
    operator_ids: OperatorScope,
    environment: OperatorEnvironment,
    target_kind: BlockTargetKind,
    status: z.enum(["active", "unblocked", "expired", "all"]),
    target_ref: z.string().min(1).max(128).optional(),
    reason: BlockReason.optional(),
    blocked_from: IsoDateTime.optional(),
    blocked_to: IsoDateTime.optional(),
    offset: z.int().min(0),
    limit: z.int().min(1).max(500),
  })
  .strict()
  .meta({ id: "ListEntryQueryV1" });
export type ListEntryQueryV1 = z.infer<typeof ListEntryQueryV1>;

export const ListEntryPageV1 = z
  .object({ total: z.int().nonnegative(), items: z.array(ListEntryV1) })
  .strict()
  .meta({ id: "ListEntryPageV1" });
export type ListEntryPageV1 = z.infer<typeof ListEntryPageV1>;

/** At most this many subjects per status query (an export batch reads 5,000 at once). */
export const MAX_LIST_ENTRY_SUBJECTS = 10_000;

const ListEntrySubjectV1 = z
  .object({
    operator_id: z.uuid(),
    target_kind: BlockTargetKind,
    target_ref: z.string().min(1).max(128),
  })
  .strict();

/**
 * Explorer → Decision Engine (`ListEntryAdministration.active`): which of these players and
 * devices are blocked at `at` (an active List Entry not past its `expires_at`), for the status
 * badges of lists, profiles and exports.
 */
export const ActiveListEntriesQueryV1 = z
  .object({
    environment: OperatorEnvironment,
    at: IsoDateTime,
    subjects: z.array(ListEntrySubjectV1).max(MAX_LIST_ENTRY_SUBJECTS),
  })
  .strict()
  .meta({ id: "ActiveListEntriesQueryV1" });
export type ActiveListEntriesQueryV1 = z.infer<typeof ActiveListEntriesQueryV1>;

/** One List Entry in force: what a badge shows (never the comment). */
export const ActiveListEntryV1 = z
  .object({
    block_id: z.uuid(),
    operator_id: z.uuid(),
    target_kind: BlockTargetKind,
    target_ref: z.string(),
    reason: BlockReason,
    blocked_at: IsoDateTime,
    expires_at: IsoDateTime.nullable(),
  })
  .strict()
  .meta({ id: "ActiveListEntryV1" });
export type ActiveListEntryV1 = z.infer<typeof ActiveListEntryV1>;

export const ActiveListEntriesV1 = z
  .object({ items: z.array(ActiveListEntryV1) })
  .strict()
  .meta({ id: "ActiveListEntriesV1" });
export type ActiveListEntriesV1 = z.infer<typeof ActiveListEntriesV1>;

/**
 * Explorer → Decision Engine (`ListEntryAdministration.blockedPlayers`): the players of the scope
 * blocked at `at`, for the map's current-status filter (Blocked / None).
 */
export const BlockedPlayersQueryV1 = z
  .object({ operator_ids: OperatorScope, environment: OperatorEnvironment, at: IsoDateTime })
  .strict()
  .meta({ id: "BlockedPlayersQueryV1" });
export type BlockedPlayersQueryV1 = z.infer<typeof BlockedPlayersQueryV1>;

export const BlockedPlayersV1 = z
  .object({
    players: z.array(z.object({ operator_id: z.uuid(), player_ref: z.string() }).strict()),
  })
  .strict()
  .meta({ id: "BlockedPlayersV1" });
export type BlockedPlayersV1 = z.infer<typeof BlockedPlayersV1>;

/**
 * Explorer → Decision Engine (`ListEntryAdministration.audit`): the audit records of the List
 * Entries (block, unblock, expiry; `core.audit_events`) of the scope, newest first by
 * `(occurred_at, id)`, strictly older than `before`, filtered by console user, action and period
 * (`from` inclusive, `to` exclusive). The Explorer merges them with its own and iam's records.
 */
export const ListEntryAuditQueryV1 = z
  .object({
    operator_ids: OperatorScope,
    environment: OperatorEnvironment,
    console_user_id: z.uuid().optional(),
    action: z
      .string()
      .regex(/^[a-z][a-z0-9_]{0,63}$/)
      .optional(),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
    before: z.object({ occurred_at: IsoDateTime, id: z.uuid() }).strict().optional(),
    limit: z.int().min(1).max(201),
  })
  .strict()
  .meta({ id: "ListEntryAuditQueryV1" });
export type ListEntryAuditQueryV1 = z.infer<typeof ListEntryAuditQueryV1>;

export const ListEntryAuditV1 = z
  .object({ records: z.array(AuditRecordDtoV1) })
  .strict()
  .meta({ id: "ListEntryAuditV1" });
export type ListEntryAuditV1 = z.infer<typeof ListEntryAuditV1>;

/** Bounds of the Tenant Policy (today's `EnvironmentPolicy` invariants in `operator-auth`). */
export const TENANT_POLICY_LIMITS = {
  maxAllowedJurisdictions: 100,
  minTravelSpeedKmh: 100,
  maxTravelSpeedKmh: 2000,
  defaultTravelSpeedKmh: 900,
} as const;

/** Fields of the Tenant Policy, as named in `400 invalid_request` and in the refusal below. */
export const TENANT_POLICY_FIELDS = [
  "allowed_jurisdictions",
  "max_travel_speed_kmh",
  "enabled_checks",
] as const;
export const TenantPolicyField = z.enum(TENANT_POLICY_FIELDS);
export type TenantPolicyField = z.infer<typeof TenantPolicyField>;

/**
 * Decision Engine → `operator-auth` / Explorer: the Tenant Policy of one operator environment
 * (`core.tenant_policies`): allowed jurisdictions (ISO 3166-1 / 3166-2, 1-100, from Location's
 * catalog), the check flags enabled (`core.jurisdiction_check`, `core.blocklist_check`) and the
 * `max_travel_speed_kmh` (100-2,000) of `travel_speed`.
 */
export const TenantPolicyV1 = z
  .object({
    operator_id: z.uuid(),
    environment: OperatorEnvironment,
    allowed_jurisdictions: z
      .array(JurisdictionCode)
      .min(1)
      .max(TENANT_POLICY_LIMITS.maxAllowedJurisdictions),
    enabled_checks: z.array(CheckFlagV1),
    max_travel_speed_kmh: z
      .int()
      .min(TENANT_POLICY_LIMITS.minTravelSpeedKmh)
      .max(TENANT_POLICY_LIMITS.maxTravelSpeedKmh),
  })
  .strict()
  .meta({ id: "TenantPolicyV1" });
export type TenantPolicyV1 = z.infer<typeof TenantPolicyV1>;

/** `operator-auth` / Explorer → Decision Engine (`TenantPolicyAdministration.get`). */
export const TenantPolicyQueryV1 = z
  .object({ operator_id: z.uuid(), environment: OperatorEnvironment })
  .strict()
  .meta({ id: "TenantPolicyQueryV1" });
export type TenantPolicyQueryV1 = z.infer<typeof TenantPolicyQueryV1>;

/**
 * `operator-auth` → Decision Engine (`TenantPolicyAdministration.configure`), forwarded by
 * `ConfigureEnvironment` from `PATCH /explorer/v1/platform/operators/{id}/environment`: the policy
 * fields that request carries (same shapes and request bounds), at least one; unspecified fields
 * keep their value. The ranges are validated by the Decision Engine, which answers
 * {@link TenantPolicyRejectedV1} naming the fields and changes nothing.
 */
export const ConfigureTenantPolicyCommandV1 = z
  .object({
    operator_id: z.uuid(),
    environment: OperatorEnvironment,
    actor: z.union([ConsoleUserActorV1, SystemActorV1]),
    allowed_jurisdictions: z
      .array(z.string().max(16))
      .max(TENANT_POLICY_LIMITS.maxAllowedJurisdictions)
      .optional(),
    enabled_checks: z.array(CheckFlagV1).max(CheckFlagV1.options.length).optional(),
    max_travel_speed_kmh: z.int().optional(),
  })
  .strict()
  .refine((command) => TENANT_POLICY_FIELDS.some((field) => command[field] !== undefined), {
    message: "at least one policy field is required",
  })
  .meta({ id: "ConfigureTenantPolicyCommandV1" });
export type ConfigureTenantPolicyCommandV1 = z.infer<typeof ConfigureTenantPolicyCommandV1>;

/** Refusal of {@link ConfigureTenantPolicyCommandV1}: the invalid fields; the policy is unchanged. */
export const TenantPolicyRejectedV1 = z
  .object({
    code: z.literal("invalid_tenant_policy"),
    invalid: z.array(TenantPolicyField).min(1),
  })
  .strict()
  .meta({ id: "TenantPolicyRejectedV1" });
export type TenantPolicyRejectedV1 = z.infer<typeof TenantPolicyRejectedV1>;
